import {
    Box,
    Button,
    Grid, Link,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableContainer,
    Typography
} from "@mui/material";
import {NavBar} from "../../organisms/NavBar/NavBar";
import React, {useEffect, useState} from "react";
import QueryInput from "../../organisms/QueryInput/QueryInput";
import {QueryResultsRow} from "./components/QueryResultsRow/QueryResultsRow";
import {QueryResultsHeaders} from "./components/QueryResultsHeaders/QueryResultsHeaders";
import {QuerySummaryTable} from "./components/QuerySummaryTable/QuerySummaryTable";
import {useLocation, useNavigate} from "react-router-dom";
import {useQuerySdqlData, useQuerySdqlSummaryData} from "../../../hooks/getNbaMatchups/querySdqlData";
import {SaveTrendDialog} from "../../organisms/SaveTrendDialog/SaveTrendDialog";
import qs from "qs";
import {NoDataFoundTable} from "../../molecules/NoDataFoundTable/NoDataFoundTable";
import {getActiveGames} from "../../../utils/helperFunctions";
import {useAuth} from "../../../AuthContext/authContext";
import {
    NBA_QUERY_EXAMPLE,
    NBA_QUERY_TEXT,
    NBA_SDQL_PARAMETERS,
    NBA_SUMMATIVE_QUERY_EXAMPLE,
    NBA_SUMMATIVE_QUERY_TEXT,
    NCAABB_QUERY_EXAMPLE,
    NCAABB_QUERY_TEXT,
    NCAABB_SDQL_PARAMETERS,
    NCAABB_SUMMATIVE_QUERY_EXAMPLE,
    NCAABB_SUMMATIVE_QUERY_TEXT,
    NCAAFB_QUERY_EXAMPLE,
    NCAAFB_QUERY_TEXT,
    NCAAFB_SDQL_PARAMETERS, NCAAFB_SUMMATIVE_QUERY_EXAMPLE,
    NCAAFB_SUMMATIVE_QUERY_TEXT,
    NFL_QUERY_EXAMPLE,
    NFL_QUERY_TEXT,
    NFL_SDQL_PARAMETERS,
    NFL_SUMMATIVE_QUERY_EXAMPLE,
    NFL_SUMMATIVE_QUERY_TEXT
} from "../../../utils/constants";
import {LoadingSummaryTable} from "../../atoms/LoadingSkeleton/LoadingSummaryTable";
import {SaveTrendButton} from "../../atoms/SaveTrendButton/SaveTrendButton";

export const QueryPage = () => {

    const [userEnteredQuery, setUserEnteredQuery] = useState('')
    const [isSaveTrendDialogOpen, setIsSaveTrendDialogOpen] = useState(false)
    const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
    const [gamesLimit, setGamesLimit] = useState(50)

    const navigate = useNavigate()
    const {pathname, search} = useLocation()
    const cleanQueryString = search.startsWith('?') ?
        search.slice(1) : search;
    const rawQueryString = cleanQueryString.replace(/\+/g, "%2B");
    const parsedParams = qs.parse(rawQueryString);

    let label = 'NBA Basketball';
    let sport: 'nba' | 'ncaabb' | 'nfl' | 'ncaafb' = 'nba';
    let sdqlParamters = NBA_SDQL_PARAMETERS;
    let sdqlQueryText = NBA_QUERY_TEXT;
    let sdqlQueryExample = NBA_QUERY_EXAMPLE;
    let sdqlSummativeQueryText = NBA_SUMMATIVE_QUERY_TEXT
    let sdqlSummativeQueryExample = NBA_SUMMATIVE_QUERY_EXAMPLE;

    if (pathname.includes('/nfl-query')) {
        label = "NFL Football"
        sport = 'nfl'
        sdqlParamters = NFL_SDQL_PARAMETERS;
        sdqlQueryText = NFL_QUERY_TEXT;
        sdqlQueryExample = NFL_QUERY_EXAMPLE;
        sdqlSummativeQueryText = NFL_SUMMATIVE_QUERY_TEXT
        sdqlSummativeQueryExample = NFL_SUMMATIVE_QUERY_EXAMPLE;
    }
    if (pathname.includes('/ncaafb-query')) {
        label = "NCAA Football"
        sport = 'ncaafb'
        sdqlParamters = NCAAFB_SDQL_PARAMETERS;
        sdqlQueryText = NCAAFB_QUERY_TEXT;
        sdqlQueryExample = NCAAFB_QUERY_EXAMPLE;
        sdqlSummativeQueryText = NCAAFB_SUMMATIVE_QUERY_TEXT
        sdqlSummativeQueryExample = NCAAFB_SUMMATIVE_QUERY_EXAMPLE;
    }
    if (pathname.includes('/ncaabb-query')) {
        label = "NCAA Basketball"
        sport = 'ncaabb'
        sdqlParamters = NCAABB_SDQL_PARAMETERS;
        sdqlQueryText = NCAABB_QUERY_TEXT;
        sdqlQueryExample = NCAABB_QUERY_EXAMPLE;
        sdqlSummativeQueryText = NCAABB_SUMMATIVE_QUERY_TEXT
        sdqlSummativeQueryExample = NCAABB_SUMMATIVE_QUERY_EXAMPLE;
    }
    const {'query-string': queryString, view} = parsedParams;
    const hasAtClause = (queryString as string)?.includes('@');

    const {
        data: sdqlData,
        error: sdqlError,
        loading: sdqlDataLoading
    } = useQuerySdqlData(queryString as string || '', sport) || {}
    const {data: sdqlSummaryData, error: sdqlSummaryError, loading: sdqlSummaryDataLoading} =
        useQuerySdqlSummaryData(queryString as string || '', sport, hasAtClause) || {}

    // console.log("sdqlSummaryData: ", sdqlSummaryData)
    const activeGames = getActiveGames(sdqlData);
    const {loggedInUserEmail} = useAuth()

    const noDataFound = queryString && !sdqlData?.sdqlQuery?.length && !sdqlSummaryData?.sdqlSummaryQuery
        && !sdqlSummaryDataLoading && !sdqlDataLoading

    const queryHasAtClause = cleanQueryString.includes('@') || cleanQueryString.includes('%40');

    const handleSubmit = () => {
        navigate(`${pathname}?query-string=${encodeURIComponent(userEnteredQuery)}`)
    }

    useEffect(() => {
        if (queryString && userEnteredQuery === '') {
            setUserEnteredQuery(queryString as string || '');
        }
    }, [queryString, userEnteredQuery]);

    let headers: string[];
    if(queryHasAtClause) {
        headers = Object.keys(sdqlData?.sdqlQuery?.[0]?.dynamicFields?.[0] || [])
    } else {
        headers = Object.keys(sdqlData?.sdqlQuery?.[0] || [])
    }

    const showSdqlInformation = !queryString && !sdqlDataLoading &&
        !sdqlData?.sdqlQuery?.[0] && !sdqlSummaryData?.sdqlSummaryQuery

    return (
        <>
            <NavBar/>
            <Box display="flex" flexDirection="row" justifyContent="space-between" >
                <Grid item container justifyContent="center" xs={12}>
                    <QueryInput
                        queryString={userEnteredQuery || ''}
                        setQueryString={setUserEnteredQuery}
                        handleSubmit={handleSubmit}
                        label={label}
                    />
                </Grid>
            </Box>
            {showSdqlInformation && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    pl={3.5}
                    pr={3.5}
                >
                    <Grid item xs={12}>
                        <Typography
                            component="p"
                            variant="body2"
                            color="dark grey"
                        >
                            <Typography variant="body2" style={{fontWeight: 800}} component="span">
                                {sport.toUpperCase()} SDQL Parameters:
                            </Typography>
                            <br />{sdqlParamters}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <Typography
                            component="p"
                            variant="body2"
                            color="dark grey"
                        >
                            <Typography
                                variant="body2"
                                style={{fontWeight: 800}}
                                component="p"
                            >
                                {sdqlQueryText}
                            </Typography>
                                {sdqlQueryExample}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <Typography
                            component="p"
                            variant="body2"
                            color="dark grey"
                        >
                            <Typography
                                variant="body2"
                                style={{fontWeight: 800}}
                                component="p"
                            >
                                {sdqlSummativeQueryText}
                            </Typography>
                                {sdqlSummativeQueryExample}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <Typography
                            component="p"
                            variant="body2"
                            color="dark grey"
                        >
                            <Typography
                                variant="body2"
                                style={{fontWeight: 800}}
                                component="p"
                            >
                                SDQL Manual:
                            </Typography>
                            For complete instructions on using the SDQL (Sports Data Query Language),
                            check out <a href='https://sdql.com/'>this guide</a>.
                        </Typography>
                    </Grid>
                </Box>
            )}
            {noDataFound ? <NoDataFoundTable/> : null}
            {sdqlSummaryDataLoading && <LoadingSummaryTable />}
            {queryString && !noDataFound &&
                sdqlSummaryData?.sdqlSummaryQuery?.map((summaryItem: any, ind: number) => {
                    return (
                        <>
                            <Grid
                                item
                                container
                                xs={12}
                                pl={3.5}
                                pr={3.5}
                                mb={2}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"

                            >
                                <SaveTrendButton
                                    handleSubmit={() => setIsSaveTrendDialogOpen(true)}
                                    queryString={queryString.toString()}
                                />
                                <Typography
                                    align="center"
                                    variant="caption"
                                    component="p"
                                    color="grey"
                                    pr={3.5}
                                    pl={2}
                                    pt={1}
                                >
                                    Records & Averages for {sport.toUpperCase()} Query String:
                                    <Typography
                                        variant="caption"
                                        style={{fontWeight: 1000}}
                                    >
                                        {` ${queryString}`}
                                    </Typography>
                                </Typography>
                            </Grid>
                            <QuerySummaryTable
                                sport={sport}
                                summaryData={summaryItem}
                                setIsSaveTrendDialogOpen={setIsSaveTrendDialogOpen}
                                queryHasAtClause={queryHasAtClause}
                                loading={sdqlSummaryDataLoading}
                                queryString={queryString.toString()}
                                key={ind}
                            />
                        </>
                    )
            })
                // <QuerySummaryTable
                //     sport={sport}
                //     summaryData={sdqlSummaryData?.sdqlSummaryQuery}
                //     setIsSaveTrendDialogOpen={setIsSaveTrendDialogOpen}
                //     queryHasAtClause={queryHasAtClause}
                //     loading={sdqlSummaryDataLoading}
                //     queryString={queryString.toString()}
                // />
            }
            {sdqlData?.sdqlQuery?.[0] ?
                <>
                    <Typography
                        align="center"
                        variant="caption"
                        component="p"
                        style={{
                            color: 'grey',
                            marginTop: '24px'
                        }}
                    >
                        Results for {sport.toUpperCase()} Query String
                    </Typography>
                    <TableContainer
                        component={Paper}
                        style={{
                            maxWidth: '96%',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            marginTop: '4px',
                            marginBottom: '32px'
                        }}
                    >
                        <Table sx={{minWidth: 650}} aria-label="simple table">
                            <QueryResultsHeaders
                                queryHasAtClause={queryHasAtClause}
                                headers={headers}
                            />
                            <TableBody>
                                {(sdqlData?.sdqlQuery)
                                    ?.map((row: any, index: number) => {
                                        if (index <= gamesLimit) return (
                                            <QueryResultsRow
                                                row={row}
                                                key={index}
                                                queryHasAtClause={queryHasAtClause}
                                            />
                                        )
                                        return null
                                    })
                                }
                            </TableBody>
                        </Table>
                        {(sdqlData?.sdqlQuery)?.length > 50 && gamesLimit < (sdqlData?.sdqlQuery)?.length && (
                            <Box display="flex" justifyContent="center">
                                <Button
                                    onClick={() => setGamesLimit(gamesLimit + 50)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'lightgrey',
                                            cursor: 'pointer',
                                            marginRight: 'auto !important',
                                            marginLeft: 'auto !important'
                                        },
                                    }}>
                                    <Typography
                                        align="center"
                                        variant="caption"
                                        component="p"
                                    >
                                        View Earlier Games &#9660;
                                    </Typography>
                                </Button>
                            </Box>
                        )}
                    </TableContainer>
                    <SaveTrendDialog
                        isOpen={isSaveTrendDialogOpen}
                        onClose={() => setIsSaveTrendDialogOpen(false)}
                        trend={userEnteredQuery}
                        sport={sport}
                        isSnackBarOpen={isSnackBarOpen}
                        isTokenValid={!!loggedInUserEmail}
                        setIsSnackBarOpen={setIsSnackBarOpen}
                        queryString={userEnteredQuery}
                        sdqlSummaryData={sdqlSummaryData}
                        activeGames={activeGames}
                    />
                    <Snackbar
                        open={isSnackBarOpen}
                        autoHideDuration={3000}
                        onClose={() => setIsSnackBarOpen(false)}
                        message="Saved trend successfully."
                    />
                </>
                : null
            }
        </>
    )
}
